import Dexie from 'dexie';
import {getUsername} from '../../../_shared/GWTBridge/invoke/genPage';
import {getLanguage} from '../../../_shared/GWTBridge/invoke/common';
import {today, getBasicUserData, isIdenticalSearch} from '../utils/personalization';
import {deserialize} from '../../../_legacy/Searches/serializes/filters';
import {serializeSearchesDlg} from '../../../_legacy/Searches/serializes/searchesList';
import {getAllSearchesFilters} from '../../../_legacy/Searches/services/searchesService';
import {setDefaultSearch} from '../../../_legacy/Searches/actions/searchesList';
import {saveCustomSearch} from '../../../_legacy/Searches/actions/filters';
import {getSearchSavedMessage} from '../../../_legacy/Searches/actions/filters';
import {showSuccessToast} from '../../../components/Toast/actions/ToastActions';
import {
    PersonalizationSearches,
    Entity,
    SearchFilter
} from '../../../services/indexDB/indexeddbServiceSearches';
import {PersonalizedMoreInfo} from '../../../services/indexDB/indexeddbServiceZoomedScreens';
import {getCompanyName} from '../../../utils/proto-api-utils/utils';

export const addCollectionDate = async (db: PersonalizationSearches | PersonalizedMoreInfo) => {
    try {
        await db.collectionDates.add({
            userName: getUsername(),
            collectionDates: [today]
        });
    } catch (e) {
        console.log('Error trying to add collectionDates', e);
    }
};

export const getUserLastCollectionDate = async (
    db: PersonalizationSearches | PersonalizedMoreInfo
) => {
    const userCollectionDates = await db.collectionDates.get({userName: getUsername()});
    if (!userCollectionDates) {
        await addCollectionDate(db);
        return today;
    } else {
        return userCollectionDates.collectionDates[0];
    }
};

export const updateCollectionDate = (db: PersonalizationSearches | PersonalizedMoreInfo) => {
    db.collectionDates
        .where('userName')
        .equals(getUsername())
        .modify(collection => {
            collection.collectionDates.unshift(today);
        })
        .catch(Dexie.ModifyError, error => {
            console.log('Error updating user collectionDates', error);
        });
};

export const getSearchesEntity = async (
    db: PersonalizationSearches,
    formName: string,
    collectionDate: string
): Promise<Entity> => {
    return db.searches.get({...getBasicUserData(), formName, collectionDate});
};

export const addNewSearchesEntity = async (
    db: PersonalizationSearches,
    formName: string,
    index: number,
    searchFilters: SearchFilter[],
    collectionDate: string
) => {
    try {
        db.searches.add({
            userName: getUsername(),
            formName,
            lang: getLanguage(),
            company: getCompanyName(),
            collectionDate,
            searches: {
                [index]: [
                    {
                        count: 1,
                        filters: searchFilters
                    }
                ]
            }
        });
    } catch (err) {
        console.log('Error trying to add new search entity', err);
    }
};

export const updateSearchEntity = async (
    db: PersonalizationSearches,
    entity: Entity,
    index: number,
    searchFilters: SearchFilter[]
) => {
    return db.searches
        .where('[userName+lang+company+formName+collectionDate]')
        .equals([
            entity.userName,
            entity.lang,
            entity.company,
            entity.formName,
            entity.collectionDate
        ])
        .modify(ent => {
            const relevantSearches = entity.searches[index];
            if (!relevantSearches) {
                // Create new searchIndex key for the current Search
                ent.searches[index] = [
                    {
                        count: 1,
                        filters: searchFilters
                    }
                ];
            } else {
                const identicalSearch = search => {
                    return isIdenticalSearch(searchFilters, search.filters);
                };

                const searchIndexToUpdate = relevantSearches.findIndex(identicalSearch);
                if (searchIndexToUpdate < 0) {
                    // Add new search filter at relevant searchIndex
                    ent.searches[index].push({
                        count: 1,
                        filters: searchFilters
                    });
                } else {
                    // Update relevant search count
                    ent.searches[index][searchIndexToUpdate].count =
                        ent.searches[index][searchIndexToUpdate].count + 1;
                }
            }
        })
        .catch(Dexie.ModifyError, error => {
            console.log('Error modifying entity', error);
        });
};

export const getSavedSearches = async (form, maxSavedSearches) => {
    const formSavedSearches: any = await getAllSearchesFilters();
    const serializeSearches = serializeSearchesDlg(form, formSavedSearches);
    const savedSearches = serializeSearches.saved.slice().filter(i => i);

    if (!serializeSearches.saved) {
        return [];
    } else if (savedSearches.length >= maxSavedSearches) {
        return savedSearches;
    } else {
        /*
        Not in scope: Cross-referencing of recent searches versus saved searches
        Before handling that take note that deserialization of saved searches is
        not exaclty the same as that of the current search
        Therefore some values need handling.
        TAKE SPECIAL NOTE to time, date & number while using the Between operator
        */

        savedSearches
            .map(search => {
                const filters = search.filters?.map(filter => {
                    const deserialized = deserialize(filter, form, true);
                    return {
                        ...deserialized,
                        fieldName: filter.column.name,
                        operator: filter.operator
                    };
                });
                return filters.sort((a, b) => (a.fieldName > b.fieldName ? 1 : -1));
            })
            .reduce((searches, searchFilters) => {
                const index = searchFilters.length;
                if (!searches[index]) {
                    searches[index] = [searchFilters];
                } else {
                    searches[index] = [...searches[index], searchFilters];
                }
                return searches;
            }, {});

        return savedSearches;
    }
};

export const saveNewSearch = async (search, name, isDefault) => {
    search.setNewName(name);
    search.setName(name);
    saveCustomSearch(search).then(res => {
        isDefault && setDefaultSearch(search);
        showSuccessToast(getSearchSavedMessage(search));
    });
};
